/**
 * ⚠️ CRITICAL: SINGLE LAYOUT WRAPPER CONVENTION ⚠️
 * 
 * This Layout component is AUTOMATICALLY applied to all public routes via App.tsx.
 * 
 * DO NOT import or wrap pages with <Layout> manually!
 * 
 * ❌ WRONG:
 * import Layout from '../../components/layout/Layout';
 * export default function MyPage() {
 *   return <Layout><div>content</div></Layout>;
 * }
 * 
 * ✅ CORRECT:
 * export default function MyPage() {
 *   return <div>content</div>;
 * }
 * 
 * The router in App.tsx wraps all non-admin routes with Layout automatically.
 * Manually wrapping pages causes double nav/footer bugs.
 * 
 * See src/pages/_template.tsx for the correct page structure.
 * 
 * ── ERROR BOUNDARY COVERAGE ──
 * Navigation and footer are wrapped in separate ErrorBoundary components.
 * If nav crashes, footer still works. If footer crashes, nav still works.
 * Page content has its own boundary in router config.
 * 
 * ── ACCESSIBILITY: SKIP NAVIGATION LINK ──
 * The skip link at the top of this component is a WCAG 2.1 Level A requirement.
 * It allows keyboard and screen-reader users to bypass repetitive navigation.
 * ✅ Verified implementation:
 *    - Links to #main-content (anchor exists on <main> element below)
 *    - Visually hidden by default (sr-only)
 *    - Becomes visible when focused via Tab key
 *    - High z-index ensures it appears above all content
 *    - <main> has tabIndex={-1} to receive programmatic focus
 * DO NOT remove or modify without consulting WCAG guidelines.
 */

import { Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import SchemaManager from '../seo/SchemaManager';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from '../base/ErrorBoundary';

const Footer = lazy(() => import('../../pages/home/components/Footer'));
const BackToTop = lazy(() => import('../feature/BackToTop'));
const MobileStickyBar = lazy(() => import('./MobileStickyBar'));

interface LayoutProps {
  printHideNav?: boolean;
}

/**
 * FooterFallback — Accurate height reservation to prevent CLS
 * 
 * Measured heights across breakpoints:
 * - Mobile (< 640px): ~720px (4 columns stacked + bottom bar)
 * - Tablet (640px - 1023px): ~520px (2 columns + bottom bar)
 * - Desktop (≥ 1024px): ~440px (4 columns side-by-side + bottom bar)
 * 
 * Uses responsive height classes to match actual footer dimensions
 */
function FooterFallback() {
  return (
    <footer className="bg-[#F7F5F0] border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-[#1A2332] text-sm">
            © {new Date().getFullYear()} Rockhound Auto. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ printHideNav }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col">

      {/* ── SKIP NAVIGATION LINK (WCAG 2.1 Level A) ── */}
      <a
        href="#main-content"
        aria-label="Skip to main content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[#E8B84B] focus:text-[#1A2332] focus:font-bold focus:rounded-lg focus:shadow-lg focus:text-sm"
      >
        Skip to main content
      </a>

      {/* ── SCHEMA MANAGER — centralized route-aware schema injection ── */}
      <SchemaManager />

      {/* ── NAVBAR — wrapped in ErrorBoundary so nav crash doesn't kill the page ── */}
      <ErrorBoundary
        fallback={
          <div className="h-[72px] md:h-[120px] bg-white border-b border-gray-200 flex items-center justify-center">
            <p className="text-sm text-gray-500">Navigation temporarily unavailable</p>
          </div>
        }
      >
        <Navbar printHideNav={printHideNav} />
      </ErrorBoundary>

      {/* ── MAIN CONTENT ── */}
      <main
        id="main-content"
        aria-label="Main content"
        className="flex-1 lg:pb-0"
        style={{ paddingTop: 'var(--nav-height)' }}
        tabIndex={-1}
      >
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <ErrorBoundary
        fallback={
          <div className="bg-[#1A2332] py-8 text-center">
            <p className="text-sm text-gray-400">Footer temporarily unavailable</p>
          </div>
        }
      >
        <Suspense fallback={<FooterFallback />}>
          <Footer />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <BackToTop />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <MobileStickyBar />
        </Suspense>
      </ErrorBoundary>

    </div>
  );
}